import {
  notificationsListSubscriptions,
  orgsGet,
  orgsListMembers,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { organizationFamily, organizationMembersAtom } from 'state'
import { useRecoilState } from 'recoil'
import { useEffect, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import { NotificationSubscription, User } from 'api/model'

function useActiveOrganization() {
  const currentOrganization = useLocalStorage<string | null>('jelled_current_organization_id', null)
  return currentOrganization
}

function useImpersonatingOrganization() {
  return useLocalStorage<string | null>('impersonating_organization_id', null)
}

function useFetchActiveOrganization() {
  const [activeOrganization] = useActiveOrganization()
  const [organization, setOrganization] = useRecoilState(organizationFamily(activeOrganization ?? ''))
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchData() {
      if (!activeOrganization) {
        return
      }

      setIsLoading(true)

      try {
        const response = await orgsGet(activeOrganization)
        setOrganization(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setOrganization])

  return {
    isLoading,
    organization,
    setOrganization,
    error,
  }
}

function useFetchMembersForOrganization(organizationId: string) {
  const [membersForOrg, setMembersForOrg] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!organizationId) {
      return
    }

    async function fetchData() {
      setIsLoading(true)

      try {
        const response = await orgsListMembers(organizationId)
        setMembersForOrg(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [organizationId, setMembersForOrg])

  return {
    isLoading,
    membersForOrg,
    error,
    setMembersForOrg,
  }
}

function useFetchOrganizationMembers() {
  const [activeOrganization] = useActiveOrganization()
  const [organizationMembers, setOrganizationMembers] = useRecoilState(organizationMembersAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchData() {
      if (!activeOrganization) {
        return
      }

      setIsLoading(true)

      try {
        const response = await orgsListMembers(activeOrganization)
        setOrganizationMembers(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setOrganizationMembers])

  return {
    activeOrganization,
    isLoading,
    organizationMembers,
    error,
    setOrganizationMembers,
  }
}

function useFetchNotificationsListSubscriptions() {
  const [activeOrganization] = useActiveOrganization()
  const [notificationSubscriptions, setNotificationSubscriptions] = useState<NotificationSubscription[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchData() {
      if (!activeOrganization) {
        return
      }

      setIsLoading(true)

      try {
        const response = await notificationsListSubscriptions(activeOrganization)
        setNotificationSubscriptions(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setNotificationSubscriptions])

  return {
    activeOrganization,
    isLoading,
    notificationSubscriptions,
    error,
    setNotificationSubscriptions,
  }
}

export {
  useActiveOrganization,
  useImpersonatingOrganization,
  useFetchActiveOrganization,
  useFetchOrganizationMembers,
  useFetchNotificationsListSubscriptions,
  useFetchMembersForOrganization,
}
